/**
 * SE2 · Customer reviews (Figma 787:302, 2026-09-22)
 *
 * Renders our own review UI over JSON from the reviews Worker (which holds the Judge.me token).
 * The section has already decided in Liquid that this product has reviews and that a Worker address
 * exists — if either were false this file would never have been requested.
 *
 * Rules worth keeping:
 *
 *   - Exactly one state container is visible at rest. Loading, list, empty and error are siblings,
 *     and showState() is the only thing that touches their hidden attribute. Four visible at once
 *     is the classic symptom of a missing hide rule and it reads as a double-render bug.
 *
 *   - Review text is never interpolated into HTML. Every string that came from a stranger goes in
 *     through textContent, including the summary and the phrases set in bold inside it.
 *
 *   - There is deliberately no matchMedia in this file. Layout is entirely CSS, so a JS breakpoint
 *     cannot drift out of step with the stylesheet.
 */
(() => {
  const REQUEST_TIMEOUT_MS = 8000;

  // U+2605 with the text-presentation selector — the same run snippets/se2-stars.liquid emits.
  const STAR = '★︎';

  /** Tiles are up to ~360px wide on desktop; 800 covers that at 2x. Thumbs sit in 72px squares. */
  const TILE_WIDTH = 800;
  const THUMB_WIDTH = 220;
  const FULL_WIDTH = 1400;
  /** The comp shows three photographs across the top (792:7). */
  const PHOTO_TILES = 3;

  const fill = (template, map) =>
    Object.entries(map).reduce((s, [k, v]) => s.split(k).join(String(v)), template || '');

  /**
   * @param {string} url
   * @param {{ timeoutMs?: number, nullOn404?: boolean }} [options] — a 404 is an answer, not a
   *   failure, for the one call that asks "does Judge.me know this product"; everything else throws.
   */
  async function getJSON(url, options = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), options.timeoutMs || REQUEST_TIMEOUT_MS);
    try {
      const res = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
      if (res.status === 404 && options.nullOn404) return null;
      if (!res.ok) throw new Error(String(res.status));
      return await res.json();
    } finally {
      clearTimeout(timer);
    }
  }

  /** A model writes the summary on a cache miss; that call gets a longer leash than the lists. */
  const SUMMARY_TIMEOUT_MS = 25000;
  /** How long the shimmer placeholder may stand in for a summary that has not arrived. */
  const SUMMARY_PENDING_MS = 8000;

  /** Judge.me's placeholder, in any of its spellings, must never be printed as a name. */
  const isPlaceholderName = (name) => /^(anon|guest\b|customer\b)/i.test(String(name || '').trim());

  /** Mirrors snippets/se2-stars.liquid. If the markup there changes, change it here too. */
  function starsEl(rating, scale, label, className) {
    const pct = Math.max(0, Math.min(100, (Number(rating) / (Number(scale) || 5)) * 100));
    const row = (cls, width) => {
      const span = document.createElement('span');
      span.className = `se2-stars__row ${cls}`;
      if (width != null) span.style.width = `${width}%`;
      span.textContent = STAR.repeat(Number(scale) || 5);
      return span;
    };
    const wrap = document.createElement('span');
    wrap.className = className ? `se2-stars ${className}` : 'se2-stars';
    if (label) {
      wrap.setAttribute('role', 'img');
      wrap.setAttribute('aria-label', label);
    } else {
      wrap.setAttribute('aria-hidden', 'true');
    }
    wrap.append(row('se2-stars__row--track'), row('se2-stars__row--fill', pct));
    return wrap;
  }

  const hasPhotos = (r) => Boolean(r.has_published_pictures) && Array.isArray(r.pictures) && r.pictures.length > 0;

  /**
   * Judge.me hands back four keys that are the same file at two widths — `small` and `compact` are
   * both width=160, `huge` and `original` both width=1024. This is an image-led layout, so the width
   * is asked for directly rather than chosen from the keys.
   */
  const pictureUrl = (pic, width) => {
    const urls = (pic && pic.urls) || {};
    const base = urls.original || urls.huge || urls.compact || urls.small || '';
    if (!base) return '';
    if (!width) return base;
    return /[?&]width=\d+/.test(base)
      ? base.replace(/([?&]width=)\d+/, `$1${width}`)
      : `${base}${base.includes('?') ? '&' : '?'}width=${width}`;
  };

  const isVerified = (r) => {
    // A string, always — the Worker guarantees it, because a boolean throws here.
    const v = String(r.verified || '').toLowerCase();
    return v.includes('buyer') || v.includes('purchase');
  };

  /** Dates follow the storefront's language, not the browser's: the rest of the card is in it. */
  const formatDate = (iso) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const lang = document.documentElement.lang || undefined;
    try {
      return new Intl.DateTimeFormat(lang, { year: 'numeric', month: 'short', day: 'numeric' }).format(d);
    } catch {
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    }
  };

  class SE2Reviews extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = '1';

      this.api = (this.dataset.api || '').replace(/\/$/, '');
      this.externalId = this.dataset.externalId;
      this.perPage = Number(this.dataset.perPage) || 6;
      this.fullThreshold = Number(this.dataset.fullThreshold) || 5;
      this.mosaicThreshold = Number(this.dataset.mosaicThreshold) || 3;
      this.scale = Number(this.dataset.scale) || 5;

      this.i18n = this.readI18n();
      this.dom = this.collect();

      this.jmId = null;
      this.page = 1;
      this.mode = 'all';
      this.items = [];
      this.totalForFilter = 0;
      this.shots = [];
      this.shotIndex = 0;
      // Every list request carries a ticket; a response whose ticket is stale is dropped, so a slow
      // "5 stars" page cannot land on top of the "4 stars" one pressed after it.
      this.ticket = 0;
      this.loadingMore = false;

      this.bind();
      this.watchReviewForm();

      // Nothing is fetched until the band is near the viewport: on a PDP it is well below the fold,
      // and a product page should not pay for reviews nobody scrolled to.
      // The root is the element that actually scrolls (audit 2026-09-29, FPDP-D-23): from 990px .page-wrapper is
      // the overflow-y:auto scroller (assets/base.css), and rootMargin never extends an intermediate scroll
      // container's clip, so with the implicit root the 400px pre-load did nothing on desktop and the band
      // started loading only once it was on screen. On a phone the document scrolls and the root stays null.
      if ('IntersectionObserver' in window) {
        const scroller = document.querySelector('.page-wrapper');
        let root = null;
        if (scroller instanceof HTMLElement && scroller.contains(this)) {
          const overflowY = getComputedStyle(scroller).overflowY;
          if (overflowY === 'auto' || overflowY === 'scroll') root = scroller;
        }
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((e) => e.isIntersecting)) {
              this.observer.disconnect();
              this.load();
            }
          },
          { root, rootMargin: '400px 0px' },
        );
        this.observer.observe(this);
      } else {
        this.load();
      }
    }

    readI18n() {
      const node = this.querySelector('[data-se2-reviews-i18n]');
      if (!node) return {};
      try {
        return JSON.parse(node.textContent);
      } catch {
        return {};
      }
    }

    collect() {
      const q = (sel) => this.querySelector(sel);
      return {
        states: Array.from(this.querySelectorAll('[data-se2-state]')),
        list: q('[data-se2-list]'),
        more: q('[data-se2-more]'),
        retry: q('[data-se2-retry]'),
        count: q('[data-se2-count]'),
        ai: q('[data-se2-ai]'),
        aiText: q('[data-se2-ai-text]'),
        aiPending: q('[data-se2-ai-pending]'),
        scale: q('[data-se2-scale]'),
        scaleList: q('[data-se2-scale-list]'),
        photos: q('[data-se2-photos]'),
        write: q('[data-se2-write]'),
        form: q('[data-se2-form]'),
        formBody: q('[data-se2-form-body]'),
        formClose: q('[data-se2-form-close]'),
        live: q('[data-se2-live]'),
        lightbox: q('[data-se2-lightbox]'),
        lightboxImg: q('[data-se2-lightbox-img]'),
        lightboxCap: q('[data-se2-lightbox-cap]'),
        lightboxClose: q('[data-se2-lightbox-close]'),
        lightboxPrev: q('[data-se2-lightbox-prev]'),
        lightboxNext: q('[data-se2-lightbox-next]'),
      };
    }

    bind() {
      if (this.dom.more) this.dom.more.addEventListener('click', () => this.loadMore());
      if (this.dom.retry) {
        this.dom.retry.addEventListener('click', () => {
          // The error panel hides as the reload starts; focus moves to the band first so it does not
          // fall to <body> under a keyboard user.
          this.tabIndex = -1;
          this.focus({ preventScroll: true });
          this.load();
        });
      }

      const box = this.dom.lightbox;
      if (box) {
        if (this.dom.lightboxClose) this.dom.lightboxClose.addEventListener('click', () => box.close());
        if (this.dom.lightboxPrev) this.dom.lightboxPrev.addEventListener('click', () => this.stepLightbox(-1));
        if (this.dom.lightboxNext) this.dom.lightboxNext.addEventListener('click', () => this.stepLightbox(1));
        box.addEventListener('keydown', (e) => {
          // Arrow keys are physical; in a right-to-left storefront "previous" is on the right.
          const rtl = getComputedStyle(box).direction === 'rtl';
          if (e.key === 'ArrowLeft') this.stepLightbox(rtl ? 1 : -1);
          else if (e.key === 'ArrowRight') this.stepLightbox(rtl ? -1 : 1);
        });
        // Clicking the backdrop closes it; clicking the figure must not.
        box.addEventListener('click', (e) => {
          if (e.target === box) box.close();
        });
      }

      const form = this.dom.form;
      if (form) {
        if (this.dom.write) this.dom.write.addEventListener('click', () => this.openReviewForm());
        if (this.dom.formClose) this.dom.formClose.addEventListener('click', () => form.close());
        form.addEventListener('click', (e) => {
          if (e.target === form) form.close();
        });
      }
    }

    /* ----------------------------------------------------------------- state */

    showState(name) {
      this.dom.states.forEach((el) => {
        el.hidden = el.dataset.se2State !== name;
      });
    }

    say(message) {
      if (this.dom.live) this.dom.live.textContent = message;
    }

    /* ------------------------------------------------------------------ load */

    /**
     * @param {number} [attempt] — 0 on the first try. One failure is retried once, silently, behind the
     *   loading skeleton before the error panel is shown (2026-09-24, client review: the band must not
     *   greet a shopper with "could not be loaded" over a single dropped request to the Worker).
     */
    async load(attempt = 0) {
      this.showState('loading');
      this.say(this.i18n.loading || '');
      this.page = 1;
      this.items = [];
      const ticket = ++this.ticket;
      try {
        if (this.jmId == null) {
          const resolved = await getJSON(
            `${this.api}/api/products?external_id=${encodeURIComponent(this.externalId)}`,
            { nullOn404: true },
          );
          this.jmId = resolved && resolved.product && resolved.product.id;
          // A product Judge.me has never seen is not an error worth shouting about — but with the
          // Liquid gate already satisfied it means the two sources disagree, so the whole section
          // goes, padding included.
          if (this.jmId == null) {
            const section = this.closest('.se2-reviews');
            (section || this).hidden = true;
            return;
          }
        }
        // The photographs are asked for alongside the counts and the first page, not after them:
        // they sit above everything else, so they should be the last thing to arrive late.
        const [counts, firstPage] = await Promise.all([this.fetchCounts(), this.fetchPage(1), this.renderPhotos()]);
        if (ticket !== this.ticket) return;
        this.renderSummary(counts);
        // Deliberately not awaited: the summary is the slowest call by far (a model writes it on a
        // cache miss), and the reviews should not wait behind it.
        this.loadSummary();
        this.items = firstPage.reviews;
        this.totalForFilter = firstPage.total_count;
        this.renderList();
      } catch (err) {
        if (ticket !== this.ticket) return;
        console.warn('[se2-reviews]', err && err.message);
        if (attempt === 0) {
          await new Promise((resolve) => setTimeout(resolve, 1500));
          if (ticket !== this.ticket) return;
          return this.load(1);
        }
        // No reviews came back, so no summary will be asked for: the placeholder must not stay behind.
        if (this.dom.aiPending) this.dom.aiPending.hidden = true;
        this.showState('error');
        this.say(this.i18n.error || '');
      }
    }

    fetchCounts() {
      return getJSON(`${this.api}/api/reviews/count?product_ids=${this.jmId}`);
    }

    /** One page for the active filter. */
    async fetchPage(page) {
      const params = new URLSearchParams({
        product_ids: String(this.jmId),
        per_page: String(this.perPage),
        page: String(page),
      });
      // rating and rating_min are mutually exclusive upstream; only one is ever sent.
      if (this.mode === '5') params.set('rating', '5');
      else if (this.mode === 'min4') params.set('rating_min', '4');
      else if (this.mode.startsWith('exact:')) params.set('rating', this.mode.slice(6));

      const data = await getJSON(`${this.api}/api/reviews?${params}`);
      return { reviews: data.reviews || [], total_count: data.total_count || 0 };
    }

    async loadMore() {
      if (this.loadingMore) return;
      this.loadingMore = true;
      const btn = this.dom.more;
      // aria-disabled rather than disabled: a disabled button drops keyboard focus to <body>.
      if (btn) {
        btn.setAttribute('aria-disabled', 'true');
        btn.classList.add('is-busy');
      }
      const ticket = ++this.ticket;
      const page = this.page + 1;
      try {
        const next = await this.fetchPage(page);
        if (ticket !== this.ticket) return;
        this.page = page;
        // Append, never replace — and only the new cards are built, so the ones on screen keep
        // their focus and scroll position.
        this.items = this.items.concat(next.reviews);
        this.appendCards(next.reviews);
      } catch (err) {
        if (ticket !== this.ticket) return;
        // The cards already on the page stay; the failure is said, not shown in their place.
        console.warn('[se2-reviews] load more', err && err.message);
        this.say(this.i18n.error || '');
      } finally {
        this.loadingMore = false;
        if (btn) {
          btn.removeAttribute('aria-disabled');
          btn.classList.remove('is-busy');
        }
      }
    }

    setFilter(mode) {
      this.mode = mode || 'all';
      this.syncFilterUI();
      this.reload();
    }

    syncFilterUI() {
      const mode = this.mode;
      this.querySelectorAll('[data-se2-hist]').forEach((b) => {
        const star = b.dataset.se2Hist;
        const on = mode === `exact:${star}` || (mode === '5' && star === '5');
        b.setAttribute('aria-pressed', String(on));
      });
    }

    async reload() {
      this.showState('loading');
      this.say(this.i18n.loading || '');
      this.page = 1;
      const ticket = ++this.ticket;
      try {
        const first = await this.fetchPage(1);
        if (ticket !== this.ticket) return;
        this.items = first.reviews;
        this.totalForFilter = first.total_count;
        this.renderList();
      } catch (err) {
        if (ticket !== this.ticket) return;
        console.warn('[se2-reviews] filter', err && err.message);
        // No reviews came back, so no summary will be asked for: the placeholder must not stay behind.
        if (this.dom.aiPending) this.dom.aiPending.hidden = true;
        this.showState('error');
        this.say(this.i18n.error || '');
      }
    }

    /* --------------------------------------------------------------- summary */

    renderSummary(counts) {
      const total = Number(counts && counts.total) || 0;
      const breakdown = (counts && counts.breakdown) || {};

      if (this.dom.count && total > 0) {
        // The Worker is authoritative; the metafield the section rendered can lag a Judge.me sync.
        this.dom.count.textContent =
          total === 1 ? this.i18n.basedOnOne || '' : fill(this.i18n.basedOn, { '%c': total.toLocaleString() });
      }

      if (total >= this.fullThreshold) {
        this.renderScale(breakdown, total);
      }
    }

    async loadSummary() {
      const host = this.dom.ai;
      if (!host) return;
      // Already written (a retry after a later failure): leave it be rather than draw the
      // placeholder beside it.
      if (!host.hidden && host.querySelector('.se2-reviews__ai-word')) return;

      // A merchant sentence, when there is one, is already in the DOM and wins outright.
      if (host.hasAttribute('data-se2-ai-fixed')) {
        host.hidden = false;
        this.animateSummary(this.dom.aiText.textContent.trim(), []);
        return;
      }

      if (this.dom.aiPending) this.dom.aiPending.hidden = false;
      // The placeholder holds the card's place for as long as a summary is plausibly on its way, not for the whole
      // 25-second leash (review 2026-09-25: an "AI review summary" label over shimmer, with nothing ever under
      // it). A summary that arrives after this still draws in.
      const pendingCap = window.setTimeout(() => {
        if (this.dom.aiPending) this.dom.aiPending.hidden = true;
      }, SUMMARY_PENDING_MS);
      try {
        const data = await getJSON(`${this.api}/api/reviews/summary?product_ids=${this.jmId}`, {
          timeoutMs: SUMMARY_TIMEOUT_MS,
        });
        window.clearTimeout(pendingCap);
        const text = data && data.summary;
        if (this.dom.aiPending) this.dom.aiPending.hidden = true;
        // No summary is an ordinary outcome on a product with a handful of reviews. Nothing is
        // drawn, rather than an apology for something the shopper never asked for.
        if (!text) return;

        host.hidden = false;
        this.animateSummary(text, Array.isArray(data.highlights) ? data.highlights : []);
      } catch (err) {
        window.clearTimeout(pendingCap);
        console.warn('[se2-reviews] summary', err && err.message);
        if (this.dom.aiPending) this.dom.aiPending.hidden = true;
      }
    }

    /**
     * Sets the summary a word at a time, with the reviewers' own phrases in bold (787:312). The
     * staggered reveal is CSS — each word carries its own index and the stylesheet delays it — so
     * with reduced motion the whole thing is simply there. Screen readers get the finished sentence
     * from the container's own text, not word by word.
     *
     * A highlight is matched once, case-insensitively, on the first occurrence not already taken;
     * the Worker has already checked that each one is a phrase from a real review.
     */
    animateSummary(text, highlights) {
      const host = this.dom.aiText;
      if (!host) return;
      host.textContent = '';

      const source = String(text);
      const ranges = [];
      for (const phrase of highlights || []) {
        const needle = String(phrase || '').trim().toLowerCase();
        if (!needle) continue;
        let from = 0;
        while (from <= source.length) {
          const at = source.toLowerCase().indexOf(needle, from);
          if (at < 0) break;
          const end = at + needle.length;
          const taken = ranges.some((r) => at < r.end && end > r.start);
          // Whole words only: "lasting scent" must not bold the tail of "everlasting scent".
          const wordEdge =
            (at === 0 || !/\p{L}/u.test(source[at - 1])) && (end === source.length || !/\p{L}/u.test(source[end]));
          if (!taken && wordEdge) {
            ranges.push({ start: at, end });
            break;
          }
          from = at + 1;
        }
      }
      ranges.sort((a, b) => a.start - b.start);

      // Plain and bold segments in order, each then split into words.
      const segments = [];
      let cursor = 0;
      for (const r of ranges) {
        if (r.start > cursor) segments.push({ text: source.slice(cursor, r.start), strong: false });
        segments.push({ text: source.slice(r.start, r.end), strong: true });
        cursor = r.end;
      }
      if (cursor < source.length) segments.push({ text: source.slice(cursor), strong: false });

      let index = 0;
      segments.forEach((seg) => {
        const parent = seg.strong ? document.createElement('strong') : host;
        if (seg.strong) parent.className = 'se2-reviews__ai-strong';
        // Leading and trailing spaces belong between words, so they are kept as text nodes.
        const parts = seg.text.split(/(\s+)/);
        parts.forEach((part) => {
          if (!part) return;
          if (/^\s+$/.test(part)) {
            parent.append(document.createTextNode(' '));
            return;
          }
          const span = document.createElement('span');
          span.className = 'se2-reviews__ai-word';
          span.style.setProperty('--i', String(index));
          span.textContent = part;
          parent.append(span);
          index += 1;
        });
        if (seg.strong) host.append(parent);
      });

      // The caret rides the last word and is dropped once the run has finished.
      host.classList.add('is-writing');
      const runMs = 90 + index * 26;
      window.setTimeout(() => host.classList.remove('is-writing'), runMs);
    }

    /**
     * The distribution (787:333). Each row is a filter as well as a reading: press a bar to see only
     * that rating, press it again to clear.
     */
    renderScale(breakdown, total) {
      const host = this.dom.scale;
      const list = this.dom.scaleList;
      if (!host || !list) return;
      list.textContent = '';

      // Keyed by star value, never by array position: positional index maths is how a redesign
      // ends up showing the 1-star count on the bar labelled 5.
      for (const star of ['5', '4', '3', '2', '1']) {
        const count = Number(breakdown[star]) || 0;
        const pct = total > 0 ? (count / total) * 100 : 0;

        const li = document.createElement('li');
        li.className = 'se2-reviews__scale-row';

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'se2-reviews__scale-btn';
        btn.dataset.se2Hist = star;
        btn.setAttribute('aria-pressed', 'false');
        // Named for what pressing it does; the reading ("55 of 66 reviews are 5 stars") is the
        // description, so a screen reader hears both, action first.
        btn.setAttribute('aria-label', fill(this.i18n.starsFilter || this.i18n.starRow, { '%s': star, '%c': count, '%t': total }));
        const reading = document.createElement('span');
        reading.className = 'se2-reviews__sr';
        reading.id = `se2-reviews-row-${star}-${Math.random().toString(36).slice(2, 7)}`;
        reading.textContent = fill(this.i18n.starRow, { '%c': count, '%t': total, '%s': star });
        btn.setAttribute('aria-describedby', reading.id);
        // A rating with no reviews behind it is a reading, not a filter.
        if (count === 0) btn.disabled = true;

        const num = document.createElement('span');
        num.className = 'se2-reviews__scale-star';
        num.append(document.createTextNode(star));
        num.append(starsEl(1, 1, ''));

        const track = document.createElement('span');
        track.className = 'se2-reviews__scale-track';
        const bar = document.createElement('span');
        bar.className = 'se2-reviews__scale-bar';
        bar.style.setProperty('--se2-hist-pct', `${pct}%`);
        track.append(bar);

        const value = document.createElement('span');
        value.className = 'se2-reviews__scale-count';
        value.textContent = count.toLocaleString();

        btn.append(num, track, value, reading);
        btn.addEventListener('click', () => {
          const already = btn.getAttribute('aria-pressed') === 'true';
          // Five stars keeps its own mode name for the Worker's exact-match query.
          this.setFilter(already ? 'all' : star === '5' ? '5' : `exact:${star}`);
        });

        li.append(btn);
        list.append(li);
      }
      host.hidden = false;
      this.syncFilterUI();
    }

    /* ---------------------------------------------------------------- photos */

    async renderPhotos() {
      const host = this.dom.photos;
      if (!host) return;

      let photoReviews = [];
      try {
        // Ask for photo reviews specifically. Sampling the newest page instead meant that a product
        // whose photos sat deeper in the list showed no photographs at all, which looked like a
        // product nobody had photographed rather than a query that had not looked far enough.
        const data = await getJSON(`${this.api}/api/reviews?product_ids=${this.jmId}&per_page=10&page=1&photos=1`);
        photoReviews = (data.reviews || []).filter(hasPhotos);
      } catch {
        return;
      }

      const shots = [];
      photoReviews.forEach((r) => {
        r.pictures.forEach((p) => {
          const small = pictureUrl(p, TILE_WIDTH);
          const large = pictureUrl(p, FULL_WIDTH);
          if (small) shots.push({ small, large, review: r });
        });
      });

      if (shots.length < this.mosaicThreshold) return;
      this.shots = shots;

      host.textContent = '';
      shots.slice(0, PHOTO_TILES).forEach((shot, i) => {
        const li = document.createElement('li');
        li.className = 'se2-reviews__photo-item';

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'se2-reviews__photo';
        btn.setAttribute('aria-label', `${this.i18n.photoLabel || 'Customer photo'} ${i + 1}`);

        const img = document.createElement('img');
        img.src = shot.small;
        img.loading = 'lazy';
        img.decoding = 'async';
        img.alt = '';

        btn.append(img);
        btn.addEventListener('click', () => this.openLightbox(i));
        li.append(btn);
        host.append(li);
      });

      host.hidden = false;
    }

    openLightbox(index) {
      const box = this.dom.lightbox;
      if (!box || !this.shots.length) return;
      this.showShot(index);
      // showModal gives the focus trap and Esc handling from the platform rather than by hand.
      if (typeof box.showModal === 'function' && !box.open) box.showModal();
    }

    stepLightbox(dir) {
      if (this.shots.length < 2) return;
      const n = this.shots.length;
      this.showShot((this.shotIndex + dir + n) % n);
    }

    showShot(index) {
      const shot = this.shots[index];
      if (!shot) return;
      this.shotIndex = index;
      const img = this.dom.lightboxImg;
      if (img) {
        img.src = shot.large || shot.small;
        img.alt = `${this.i18n.photoLabel || ''} ${index + 1} / ${this.shots.length}`.trim();
        // The markup carries a square placeholder so the ratio is declared before load; swap in the
        // real one as soon as it is known, so a tall photo does not open as a square and reflow.
        img.addEventListener(
          'load',
          () => {
            if (img.naturalWidth && img.naturalHeight) {
              img.width = img.naturalWidth;
              img.height = img.naturalHeight;
            }
          },
          { once: true },
        );
      }
      if (this.dom.lightboxCap) {
        const name = (shot.review.reviewer && shot.review.reviewer.name) || '';
        const counter = this.shots.length > 1 ? `${index + 1} / ${this.shots.length}` : '';
        this.dom.lightboxCap.textContent = [name, counter].filter(Boolean).join(' · ');
      }
      const many = this.shots.length > 1;
      if (this.dom.lightboxPrev) this.dom.lightboxPrev.hidden = !many;
      if (this.dom.lightboxNext) this.dom.lightboxNext.hidden = !many;
    }

    /* ----------------------------------------------------------- review form */

    /**
     * Judge.me's app embed fills the widget in our dialog on its own schedule. The button is shown
     * only once something that can take a review is there, so a shop with the embed switched off
     * gets no button rather than an empty dialog.
     */
    watchReviewForm() {
      const body = this.dom.formBody;
      const btn = this.dom.write;
      if (!body || !btn) return;
      const ready = () => Boolean(body.querySelector('.jdgm-write-rev-link, .jdgm-form-wrapper, form'));
      if (ready()) {
        btn.hidden = false;
        return;
      }
      if (!('MutationObserver' in window)) return;
      const mo = new MutationObserver(() => {
        if (ready()) {
          btn.hidden = false;
          mo.disconnect();
        }
      });
      mo.observe(body, { childList: true, subtree: true });
    }

    openReviewForm() {
      const form = this.dom.form;
      const body = this.dom.formBody;
      if (!form || !body) return;
      if (typeof form.showModal === 'function' && !form.open) form.showModal();
      // Their widget opens its form from a "Write a review" link; press it if the form is not up.
      const wrapper = body.querySelector('.jdgm-form-wrapper');
      const link = body.querySelector('.jdgm-write-rev-link');
      const visible = wrapper && wrapper.getClientRects().length > 0;
      if (link && !visible) link.click();
    }

    /* ------------------------------------------------------------------ list */

    renderList() {
      const host = this.dom.list;
      if (!host) return;

      if (!this.items.length) {
        this.showState('empty');
        this.say(this.i18n.empty || '');
        return;
      }

      host.textContent = '';
      this.items.forEach((r) => host.append(this.card(r)));
      this.syncMore();
      this.showState('list');
      this.say(fill(this.i18n.showing, { '%c': this.items.length, '%t': this.totalForFilter }));
    }

    /** Load more: the new cards go under the old ones, and focus lands on the first of them. */
    appendCards(reviews) {
      const host = this.dom.list;
      if (!host) return;
      let first = null;
      reviews.forEach((r) => {
        const li = this.card(r);
        if (!first) first = li;
        host.append(li);
      });
      this.syncMore();
      if (first) {
        first.tabIndex = -1;
        first.focus({ preventScroll: true });
      }
      this.say(fill(this.i18n.showing, { '%c': this.items.length, '%t': this.totalForFilter }));
    }

    syncMore() {
      if (!this.dom.more) return;
      this.dom.more.hidden = !(this.totalForFilter > this.items.length);
    }

    /** One review card (787:375): who and when, the stars, then the title and the text. */
    card(r) {
      const li = document.createElement('li');
      li.className = 'se2-reviews__panel se2-reviews__card';

      const head = document.createElement('div');
      head.className = 'se2-reviews__card-head';

      // Name, else place, else nothing. The Worker has already stripped Judge.me's "Anonymous"
      // placeholder to an empty string; printing it would be a system default leaking onto the page.
      const rawName = (r.reviewer && r.reviewer.name) || '';
      const name = isPlaceholderName(rawName) ? '' : rawName;
      const place = (r.reviewer && r.reviewer.location) || '';
      const who = name || place;
      if (who) {
        const nameEl = document.createElement('p');
        nameEl.className = 'se2-reviews__card-name';
        nameEl.textContent = who;
        head.append(nameEl);
      }

      const meta = document.createElement('p');
      meta.className = 'se2-reviews__card-meta';
      if (isVerified(r)) {
        const badge = document.createElement('span');
        badge.className = 'se2-reviews__badge';
        badge.textContent = this.i18n.verified || '';
        meta.append(badge);
      }
      const when = r.created_at ? formatDate(r.created_at) : '';
      if (when) {
        if (meta.childNodes.length) {
          const dot = document.createElement('span');
          dot.className = 'se2-reviews__dot';
          dot.setAttribute('aria-hidden', 'true');
          meta.append(dot);
        }
        const time = document.createElement('time');
        time.className = 'se2-reviews__card-date';
        time.dateTime = r.created_at;
        time.textContent = when;
        meta.append(time);
      }
      if (meta.childNodes.length) head.append(meta);

      head.append(
        starsEl(
          r.rating,
          this.scale,
          fill(this.i18n.ratingLabel, { '%r': r.rating, '%m': this.scale }),
          'se2-reviews__card-stars',
        ),
      );
      li.append(head);

      if (r.title || r.body) {
        const body = document.createElement('div');
        body.className = 'se2-reviews__card-body';
        if (r.title) {
          const h = document.createElement('p');
          h.className = 'se2-reviews__card-title';
          h.textContent = r.title;
          body.append(h);
        }
        if (r.body) {
          const text = document.createElement('p');
          text.className = 'se2-reviews__card-text';
          text.textContent = r.body;
          body.append(text);
        }
        li.append(body);
      }

      if (hasPhotos(r)) {
        const strip = document.createElement('ul');
        strip.className = 'se2-reviews__card-pics';
        r.pictures.forEach((p, i) => {
          const small = pictureUrl(p, THUMB_WIDTH);
          if (!small) return;
          const item = document.createElement('li');
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'se2-reviews__card-pic';
          btn.setAttribute('aria-label', `${this.i18n.photoLabel || 'Customer photo'} ${i + 1}`);
          const img = document.createElement('img');
          img.src = small;
          img.loading = 'lazy';
          img.decoding = 'async';
          img.alt = '';
          btn.append(img);
          btn.addEventListener('click', () => {
            // Open on this photograph if it is among the ones the lightbox already walks; otherwise
            // put it first so the arrows still reach the rest.
            const large = pictureUrl(p, FULL_WIDTH);
            let at = this.shots.findIndex((s) => s.large === large);
            if (at < 0) {
              this.shots.unshift({ small, large, review: r });
              at = 0;
            }
            this.openLightbox(at);
          });
          item.append(btn);
          strip.append(item);
        });
        if (strip.children.length) li.append(strip);
      }

      return li;
    }
  }

  if (!customElements.get('se2-reviews')) customElements.define('se2-reviews', SE2Reviews);
})();
